import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CircularProgress } from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import AuthContext from "../../../context/auth-context";
import {
    RuleHead, RuleTitle, RuleMeta, RuleActions, Btn, FormGrid, Hint, Field, Row2,
    CeBox, CeHead, CeBody, CeSection, SelectWrap, Baseline, BaselineTitle, BaselineSub,
    DatePickerInput
} from "./styles";

const ALLOWED_EVENT_TYPES = ["credit_analysis_natural_person", "credit_analysis_legal_person"];

function CreateBacktest() {
    const { t } = useTranslation();
    let history = useHistory()

    let user_data = useContext(AuthContext).user_data
    let roles = user_data.roles
    if (!roles.includes("backtest_creator")) {
        history.push("/backtests-inbox")
    }

    const [name, setName] = useState("")
    const [initialDate, setInitialDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [selectedEvent, setSelectedEvent] = useState("")
    const [selectedRuleKey, setSelectedRuleKey] = useState("")
    const [selectedVersion, setSelectedVersion] = useState("")

    const [company, setCompany] = useState(null)
    const [companies, setCompanies] = useState(null)
    const [rulesState, setRulesState] = useState({ loading: false, data: null })
    const [countState, setCountState] = useState({ loading: false, data: null })
    const [submitting, setSubmitting] = useState(false)
    const [snackBar, setSnackBar] = useState({ opened: false, severity: "error", message: "" })

    const isBusinessGroup = !!user_data.business_group_key

    const companyHeaders = useMemo(() => {
        if (!isBusinessGroup || !company?.company_key) return {}
        return { headers: { "COMPANY-KEY": company.company_key } }
    }, [isBusinessGroup, company])

    useEffect(() => {
        if (isBusinessGroup) {
            axios.get("/dash/rule_engine/companies").then(response => {
                setCompanies(response.data)
            }).catch(() => {
                setCompanies(undefined)
            })
            return
        }
        axios.get("/dash/rule_engine/company").then(response => {
            setCompany(response.data)
        }).catch(() => {
            setCompany(undefined)
        })
    }, [isBusinessGroup])

    const fetchEventRules = useCallback((eventEnum) => {
        setRulesState({ loading: true, data: null })
        const fetchAllPages = async () => {
            let allRules = []
            let pageNumber = 0
            let numberOfPages = 1
            while (pageNumber < numberOfPages) {
                const response = await axios.get("/dash/rule_engine/rules", {
                    params: { page_number: pageNumber, page_rows: 15, event_type: eventEnum }
                })
                allRules = allRules.concat(response.data.data || [])
                numberOfPages = parseInt(response.data.number_of_pages) || 1
                pageNumber++
            }
            return allRules
        }
        fetchAllPages().then(allRules => {
            setRulesState({ loading: false, data: allRules })
        }).catch(() => {
            setRulesState({ loading: false, data: [] })
        })
    }, [])

    useEffect(() => {
        if (selectedEvent) {
            fetchEventRules(selectedEvent)
        } else {
            setRulesState({ loading: false, data: null })
        }
    }, [selectedEvent, fetchEventRules])

    const toIsoStartOfDay = (date) => moment(date).format("YYYY-MM-DD") + "T00:00:00Z"
    const toIsoEndOfDay = (date) => moment(date).format("YYYY-MM-DD") + "T23:59:59Z"

    const fetchCount = useCallback((eventEnum, from, to) => {
        setCountState({ loading: true, data: null })
        axios.get("/dash/backtest/company_event/" + eventEnum + "/count", {
            ...companyHeaders,
            params: {
                initial_date: toIsoStartOfDay(from),
                end_date: toIsoEndOfDay(to)
            }
        }).then(response => {
            setCountState({ loading: false, data: response.data })
        }).catch(() => {
            setCountState({ loading: false, data: undefined })
        })
    }, [companyHeaders])

    useEffect(() => {
        if (selectedEvent && initialDate && endDate) {
            fetchCount(selectedEvent, initialDate, endDate)
        } else {
            setCountState({ loading: false, data: null })
        }
    }, [selectedEvent, initialDate, endDate, fetchCount])

    const companyOptions = (() => {
        if (!companies) return []
        let options = []
        companies.forEach(item => {
            if (item?.company_key) {
                options.push({ company_key: item.company_key, name: item.name })
            }
        })
        return options.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    })()

    const eventOptions = (() => {
        if (!company?.company_events) return []
        let seen = {}
        let options = []
        company.company_events.forEach(event => {
            let eventType = event.field_mapping?.event_type
            if (eventType?.enum && ALLOWED_EVENT_TYPES.includes(eventType.enum) && !seen[eventType.enum]) {
                seen[eventType.enum] = true
                options.push({ enum: eventType.enum, description: eventType.description })
            }
        })
        return options.sort((a, b) => (a.description || "").localeCompare(b.description || ""))
    })()

    const candidateRules = (() => {
        let rules = rulesState.data || []
        if (!isBusinessGroup || !company?.company_key) return rules
        let ownedRules = []
        rules.forEach(rule => {
            if (rule.company_key === company.company_key) {
                ownedRules.push(rule)
            }
        })
        return ownedRules
    })()
    const selectedRule = candidateRules.find(rule => rule.rule_key === selectedRuleKey)

    const versionOptions = (() => {
        if (!selectedRule) return []
        let options = []
        for (let version = selectedRule.version; version >= 1; version--) {
            options.push({ value: String(version), label: `${t("Versão")} ${version}` })
        }
        if (selectedRule.has_rule_draft) {
            options.unshift({ value: "draft", label: t("Rascunho") })
        }
        return options
    })()

    const activeRuleKey = (() => {
        if (!company?.company_events) return null
        let event = company.company_events.find(e => e.field_mapping?.event_type?.enum === selectedEvent)
        return event?.active_rule_key || null
    })()

    const baselineRule = activeRuleKey ? candidateRules.find(rule => rule.rule_key === activeRuleKey) : null

    const handleCompanyChange = (event) => {
        let chosenCompany = null
        companies.forEach(item => {
            if (item?.company_key === event.target.value) {
                chosenCompany = item
            }
        })
        setCompany(chosenCompany)
        setSelectedEvent("")
        setSelectedRuleKey("")
        setSelectedVersion("")
        setRulesState({ loading: false, data: null })
        setCountState({ loading: false, data: null })
    }

    const handleEventChange = (event) => {
        setSelectedEvent(event.target.value)
        setSelectedRuleKey("")
        setSelectedVersion("")
    }

    const handleRuleChange = (event) => {
        setSelectedRuleKey(event.target.value)
        setSelectedVersion("")
    }

    const showSnackBar = (message) => {
        setSnackBar({ opened: true, severity: "error", message: message })
    }

    const handleSubmit = () => {
        if (submitting) return
        if (!name) return showSnackBar("Informe o nome da análise")
        if (isBusinessGroup && !company) return showSnackBar("Selecione a empresa")
        if (!initialDate || !endDate) return showSnackBar("Informe o período de análise")
        if (moment(endDate).isBefore(moment(initialDate), "day")) return showSnackBar("A data final não pode ser anterior à data inicial")
        if (!selectedEvent) return showSnackBar("Selecione o evento")
        if (!selectedRule) return showSnackBar("Selecione a regra candidata")
        if (!selectedVersion) return showSnackBar("Selecione a versão a testar")
        if (!baselineRule) return showSnackBar("Não há regra ativa em produção para esse evento")

        let payload = {
            name: name,
            event_type: selectedEvent,
            rule_key_current: baselineRule.rule_key,
            rule_version_current: baselineRule.version,
            rule_key_candidate: selectedRule.rule_key,
            rule_version_candidate: selectedVersion === "draft" ? null : Number(selectedVersion),
            filters: {
                initial_date: toIsoStartOfDay(initialDate),
                end_date: toIsoEndOfDay(endDate)
            },
            analyst_data: {
                analyst_email: user_data.email,
                analyst_name: user_data.name,
                analyst_key: user_data.user_key,
                analyst_document_number: user_data.document_number
            }
        }

        setSubmitting(true)
        axios.post("/dash/backtest/backtest", payload, companyHeaders).then(() => {
            history.push("/backtests-inbox")
        }).catch(() => {
            setSubmitting(false)
            showSnackBar("Não foi possível criar o backtest")
        })
    }

    const overLimit = countState.data && countState.data.count > countState.data.max_analysis_per_backtest
    const dateFormat = user_data.locale === "en" ? "MM/dd/yyyy" : "dd/MM/yyyy"
    const lastSelectableDate = moment().subtract(1, "day").endOf("day").toDate()

    const renderCountHint = () => {
        if (!selectedEvent || !initialDate || !endDate) {
            return <Hint>{t("Selecione o período e o evento para estimar os casos")}</Hint>
        }
        if (countState.loading) {
            return <Hint>{t("Estimando casos")}...</Hint>
        }
        if (countState.data === undefined) {
            return <Hint>{t("Não foi possível estimar os casos")}</Hint>
        }
        if (countState.data && overLimit) {
            return (
                <Hint overLimit>
                    {t("Utilizando os últimos")} <b>{countState.data.max_analysis_per_backtest}</b>
                    {" "}{t("casos de um total disponível de")} <b>{countState.data.count}</b> {t("casos")}
                </Hint>
            )
        }
        if (countState.data) {
            return (
                <Hint>
                    ≈ <b>{countState.data.count}</b> {t("casos no período selecionado")}
                </Hint>
            )
        }
        return null
    }

    const loadingContext = isBusinessGroup ? companies === null : company === null

    if (loadingContext) {
        return (
            <div style={{ width: "auto" }}>
                <RuleHead><RuleTitle>{t("Criar Backtest")}</RuleTitle></RuleHead>
                <div className="circularProgressCenter"><CircularProgress /></div>
            </div>
        )
    }

    return (
        <div style={{ width: "auto", paddingBottom: "30px" }}>
            <RuleHead>
                <div>
                    <RuleTitle>{t("Criar Backtest")}</RuleTitle>
                    <RuleMeta>{t("Configure a entrada de dados e a regra que será testada.")}</RuleMeta>
                </div>
                <RuleActions>
                    <Btn variant="border" onClick={() => history.push("/backtests-inbox")}>
                        {t("Voltar")}
                    </Btn>
                    <Btn onClick={handleSubmit} disabled={submitting}>
                        {t("Executar Backtest")}
                    </Btn>
                </RuleActions>
            </RuleHead>

            <FormGrid>
                <Field>
                    <label>{t("Nome da análise")}</label>
                    <input
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder={t("ex: Backtest de crédito PF")}
                    />
                </Field>

                <CeBox>
                    <CeHead>
                        <AccountTreeIcon style={{ fontSize: "20px" }} />
                        {t("Evento")}
                    </CeHead>
                    <CeBody>
                        <CeSection>{t("Período dos dados históricos")}</CeSection>
                        <Row2>
                            <Field>
                                <label>{t("De")}</label>
                                <DatePicker
                                    selected={initialDate}
                                    onChange={(date) => setInitialDate(date)}
                                    dateFormat={dateFormat}
                                    placeholderText={t("De")}
                                    maxDate={endDate || lastSelectableDate}
                                    customInput={<DatePickerInput />}
                                />
                            </Field>
                            <Field>
                                <label>{t("Até")}</label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    dateFormat={dateFormat}
                                    placeholderText={t("Até")}
                                    minDate={initialDate}
                                    maxDate={lastSelectableDate}
                                    customInput={<DatePickerInput />}
                                />
                            </Field>
                        </Row2>
                        {renderCountHint()}

                        {isBusinessGroup && (
                            <Field style={{ marginTop: "18px" }}>
                                <label>{t("Empresa")}</label>
                                <SelectWrap>
                                    <select value={company?.company_key || ""} onChange={handleCompanyChange}>
                                        <option value="" disabled>{t("Selecione a empresa")}...</option>
                                        {companyOptions.map(option => (
                                            <option key={option.company_key} value={option.company_key}>{option.name}</option>
                                        ))}
                                    </select>
                                </SelectWrap>
                            </Field>
                        )}

                        <Field style={{ marginTop: "18px" }}>
                            <label>{t("Evento")}</label>
                            <SelectWrap>
                                <select value={selectedEvent} onChange={handleEventChange} disabled={isBusinessGroup && !company}>
                                    <option value="" disabled>{t("Selecione o evento")}...</option>
                                    {eventOptions.map(option => (
                                        <option key={option.enum} value={option.enum}>{option.description}</option>
                                    ))}
                                </select>
                                <ExpandMoreIcon className="selectwrap-chevron" />
                            </SelectWrap>
                        </Field>

                        <Field>
                            <label>{t("Regra candidata")}</label>
                            <SelectWrap>
                                <select value={selectedRuleKey} onChange={handleRuleChange} disabled={!selectedEvent || rulesState.loading}>
                                    <option value="" disabled>
                                        {!selectedEvent && `${t("Selecione o evento primeiro")}...`}
                                        {selectedEvent && rulesState.loading && `${t("Carregando")}...`}
                                        {selectedEvent && !rulesState.loading && `${t("Selecione a regra")}...`}
                                    </option>
                                    {candidateRules.map(rule => (
                                        <option key={rule.rule_key} value={rule.rule_key}>{rule.name}</option>
                                    ))}
                                </select>
                                <ExpandMoreIcon className="selectwrap-chevron" />
                            </SelectWrap>
                        </Field>

                        <Field>
                            <label>{t("Versão a testar")}</label>
                            <SelectWrap>
                                <select value={selectedVersion} onChange={(event) => setSelectedVersion(event.target.value)} disabled={!selectedRule}>
                                    <option value="" disabled>
                                        {selectedRule ? `${t("Selecione a versão")}...` : `${t("Selecione a regra primeiro")}...`}
                                    </option>
                                    {versionOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <ExpandMoreIcon className="selectwrap-chevron" />
                            </SelectWrap>
                        </Field>

                        {selectedRule && baselineRule && (
                            <Baseline>
                                <CompareArrowsIcon className="baseline-icon" />
                                <div>
                                    <BaselineTitle>{t("Comparando contra a Regra ativa em produção")}</BaselineTitle>
                                    <BaselineSub>
                                        <b>{baselineRule.name}</b> · {t("Versão")} {baselineRule.version} ({t("atual")})
                                        <span className="baseline-uuid">{baselineRule.rule_key}</span>
                                    </BaselineSub>
                                </div>
                            </Baseline>
                        )}
                        {selectedRule && !baselineRule && (
                            <Hint error>
                                {t("Não há regra ativa em produção para esse evento")}
                            </Hint>
                        )}
                    </CeBody>
                </CeBox>

                <Hint style={{ marginTop: "8px" }}>
                    {t("O resultado não sai na hora — o relatório fica disponível na lista de Backtests.")}
                </Hint>
            </FormGrid>

            <Snackbar
                open={snackBar.opened}
                autoHideDuration={8000}
                onClose={() => setSnackBar({ ...snackBar, opened: false })}
            >
                <MuiAlert elevation={6} variant="filled" onClose={() => setSnackBar({ ...snackBar, opened: false })} severity={snackBar.severity}>
                    <div className={["normalText", "subtitleSize", "bold"].join(" ")}>{t(snackBar.message)}</div>
                </MuiAlert>
            </Snackbar>
        </div>
    )
}

export default CreateBacktest
